import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Youtube, Linkedin, Apple, Smartphone } from 'lucide-react';

export function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="mt-12 md:mt-16">
      {/* ===================== MOBILE / TABLET FOOTER (<lg) ===================== */}
      <div className="lg:hidden">
        {/* App Download CTA */}
        <div className="bg-muted/30 border-t border-border">
          <div className="container px-5 py-8">
            <h3
              className="text-2xl font-bold text-primary leading-tight"
              style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
            >
              Get the Ethio Real Estates App
            </h3>
            <p
              className="mt-2 text-sm text-foreground/75 max-w-md"
              style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
            >
              Buy and Rent Property faster and better using our app.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a
                href="#"
                aria-label="Download on the App Store"
                className="flex items-center gap-2 bg-black text-white rounded-lg px-4 py-2.5 hover:bg-black/85 transition-colors"
              >
                <Apple className="h-6 w-6" />
                <div className="flex flex-col leading-tight text-left">
                  <span className="text-[10px] opacity-80">Download on the</span>
                  <span className="text-sm font-semibold">App Store</span>
                </div>
              </a>
              <a
                href="#"
                aria-label="Get it on Google Play"
                className="flex items-center gap-2 bg-black text-white rounded-lg px-4 py-2.5 hover:bg-black/85 transition-colors"
              >
                <Smartphone className="h-6 w-6" />
                <div className="flex flex-col leading-tight text-left">
                  <span className="text-[10px] opacity-80">GET IT ON</span>
                  <span className="text-sm font-semibold">Google Play</span>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Dark social + copyright band */}
        <div className="bg-black text-white">
          <div className="container px-5 py-6">
            <div className="flex items-center gap-3">
              {[
                { icon: Facebook, href: '#', label: 'Facebook', bg: 'bg-[#1877F2]' },
                { icon: Instagram, href: '#', label: 'Instagram', bg: 'bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5]' },
                { icon: Youtube, href: '#', label: 'YouTube', bg: 'bg-[#FF0000]' },
                { icon: Twitter, href: '#', label: 'Twitter', bg: 'bg-[#1DA1F2]' },
                { icon: Linkedin, href: '#', label: 'LinkedIn', bg: 'bg-[#0A66C2]' },
              ].map(({ icon: Icon, href, label, bg }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className={`flex items-center justify-center w-11 h-11 rounded-full text-white ${bg} hover:opacity-90 transition-opacity`}
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
            <p
              className="mt-4 text-sm text-white/70"
              style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
            >
              © Copyright 2007 – {year} Ethio Real Estates. All Rights Reserved
            </p>
          </div>
        </div>
      </div>

      {/* ===================== DESKTOP FOOTER (≥lg) ===================== */}
      <div className="hidden lg:block bg-primary text-primary-foreground">
        {/* Main Footer */}
        <div className="container py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Brand Column */}
            <div className="sm:col-span-2 lg:col-span-1">
              <p
                className="text-[14px] leading-relaxed text-primary-foreground/95 max-w-xs"
                style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 400 }}
              >
                Ethiopia's leading real estate platform for buying, renting, and discovering new developments across the country.
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-[13px] text-primary-foreground/95">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span>+251 911 000 000</span>
                </div>
                <div className="flex items-center gap-2 text-[13px] text-primary-foreground/95">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span>info@ethiorealestates.com</span>
                </div>
                <div className="flex items-center gap-2 text-[13px] text-primary-foreground/95">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span>Addis Ababa, Ethiopia</span>
                </div>
              </div>
              {/* Social Media Icons */}
              <div className="mt-5 flex items-center gap-3">
                {[
                  { icon: Facebook, href: '#', label: 'Facebook' },
                  { icon: Instagram, href: '#', label: 'Instagram' },
                  { icon: Twitter, href: '#', label: 'Twitter' },
                  { icon: Youtube, href: '#', label: 'YouTube' },
                  { icon: Linkedin, href: '#', label: 'LinkedIn' },
                ].map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    aria-label={label}
                    className="flex items-center justify-center w-9 h-9 rounded-full border border-primary-foreground/30 text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground transition-colors"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Properties */}
            <div>
              <h3
                className="text-[14px] font-bold uppercase tracking-widest text-primary-foreground/90 mb-3"
                style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
              >
                Properties
              </h3>
              <div className="space-y-2">
                {[
                  { to: '/buy', label: t('nav.buy') },
                  { to: '/rent', label: t('nav.rent') },
                  { to: '/projects', label: t('nav.projects') },
                  { to: '/plots', label: 'Plots & Land' },
                  { to: '/buy?type=commercial', label: 'Commercial' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block text-[14px] text-primary-foreground/95 hover:text-primary-foreground transition-colors"
                    style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 400 }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Company */}
            <div>
              <h3
                className="text-[14px] font-bold uppercase tracking-widest text-primary-foreground/90 mb-3"
                style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
              >
                Company
              </h3>
              <div className="space-y-2">
                {[
                  { to: '/agencies', label: 'Agencies' },
                  { to: '/projects', label: t('nav.projects') },
                  { to: '/advertise', label: 'Advertise' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block text-[14px] text-primary-foreground/95 hover:text-primary-foreground transition-colors"
                    style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 400 }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-foreground/10">
          <div className="container py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p
              className="text-[13px] text-primary-foreground/90"
              style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 400 }}
            >
              {t('footer.copyright')}
            </p>
          </div>
        </div>

      </div>
    </footer>
  );
}
